import { AssetTypeRepository } from '@telespot/domain';

export interface RetrieveAssetTypeOutput {
  id: string;
  name: string;
  description: string;
  type: 'image' | 'video' | 'text' | 'audio';
  version: string;
  deviceTypeIds: string[];
}

export interface IRetrieveAssetTypeQuery {
  /**
   * Retrieves a single asset type by id, projected to the fields needed by
   * the editor UI. Repository errors propagate so the caller can update its
   * error state, and an id that resolves to nothing raises
   * `AssetTypeNotFoundError` — the editor cannot open without an asset type.
   */
  execute(assetTypeId: string): Promise<RetrieveAssetTypeOutput>;
}

/** The requested asset type does not exist (or is soft-deleted). */
export class AssetTypeNotFoundError extends Error {
  constructor(public readonly assetTypeId: string) {
    super(`Asset type ${assetTypeId} not found`);
    this.name = 'AssetTypeNotFoundError';
    Object.setPrototypeOf(this, AssetTypeNotFoundError.prototype);
  }
}

export class RetrieveAssetTypeQuery implements IRetrieveAssetTypeQuery {
  constructor(private readonly assetTypeRepository: AssetTypeRepository) {}

  public async execute(assetTypeId: string): Promise<RetrieveAssetTypeOutput> {
    const assetType = await this.assetTypeRepository.getById(assetTypeId);

    // The repository answers `undefined` for a missing row, so this projection
    // has to say so explicitly instead of dereferencing it.
    if (!assetType) throw new AssetTypeNotFoundError(assetTypeId);

    return {
      id: assetType.id,
      name: assetType.name,
      description: assetType.description,
      type: assetType.type,
      version: assetType.version,
      deviceTypeIds: [...assetType.deviceTypeIds],
    };
  }
}
